/*
  "Your Ride" for Make Your Own Case: the picker state, and the gallery filter
  that used to be done in Liquid.

  Background. The ride was the second variant option, which made the product
  55 sizes x 3 rides x 3 case types = 495 variants. Liquid returns at most 250,
  so the theme saw only the first 28 sizes and no Samsung at all. The ride is a
  line-item property now (MYOC-VARIANT-CAP-BRIEF.md), which means:

    - the selected variant no longer says which ride is showing, so
      product-section.liquid renders all three sets of images and this picks one
    - changing the ride must NOT fetch a variant, so the change event is stopped
      before <variant-picker> can hear it

  Slides are DETACHED, not hidden. Embla measures its slide nodes; a
  display:none slide is 0px wide and lands on its neighbour's snap point, which
  leaves scrollNext() sitting still on a carousel that looks like it has more to
  show. Detaching keeps exactly one snap per visible image. The removed nodes
  are kept in ORIGINAL so switching back is free -- no refetch, and nothing else
  on the page moves.
*/
(function () {
  'use strict';

  if (window.__myocRide) return;
  window.__myocRide = true;

  var ROOT_SELECTOR = '[data-myoc-ride-root]';
  var INPUT_SELECTOR = '[data-myoc-ride-input]';
  var TAGGED_SELECTOR = '[data-variant*="your_ride_"]';
  var RIDE_RE = /your_ride_([a-z]+)/;

  // Switches off the pre-script CSS gate in myoc-ride.css. First act, so a
  // failure below leaves the page on the default ride rather than showing all
  // three sets at once.
  document.documentElement.setAttribute('data-myoc-ride-js', '');

  var current = null;
  var ORIGINAL = new WeakMap();
  var parents = [];

  function rideOf(el) {
    var m = RIDE_RE.exec((el.getAttribute('data-variant') || '').toLowerCase());
    return m ? m[1] : null;
  }

  function defaultRide() {
    var root = document.querySelector(ROOT_SELECTOR);
    return (root && root.getAttribute('data-myoc-ride-default')) || 'Ski';
  }

  // The full child list as the server sent it, captured the first time we see a
  // container and never recaptured: after the first pass the DOM is already a
  // subset, so re-reading it would lose the other two rides for good.
  function originalChildren(parent) {
    var list = ORIGINAL.get(parent);
    if (!list) {
      list = Array.prototype.slice.call(parent.children);
      ORIGINAL.set(parent, list);
    }
    return list;
  }

  function collectParents() {
    var tagged = document.querySelectorAll(TAGGED_SELECTOR);
    for (var i = 0; i < tagged.length; i++) {
      var p = tagged[i].parentNode;
      if (p && p.nodeType === 1 && parents.indexOf(p) === -1) {
        parents.push(p);
        originalChildren(p);
      }
    }
    parents = parents.filter(function (p) {
      return p.isConnected;
    });
  }

  function sameOrder(live, wanted) {
    if (live.length !== wanted.length) return false;
    for (var i = 0; i < wanted.length; i++) {
      if (live[i] !== wanted[i]) return false;
    }
    return true;
  }

  // Thumbnails address main-carousel slides by data-dot-index, so the indices
  // have to be closed up after a rebuild or clicking thumb 3 scrolls to a slide
  // that is no longer there.
  function renumberDots(parent) {
    var index = 0;
    for (var i = 0; i < parent.children.length; i++) {
      var child = parent.children[i];
      if (!child.hasAttribute('data-carousel-dot')) continue;
      child.setAttribute('data-dot-index', String(index));
      child.classList.toggle('is-primary', index === 0);
      index += 1;
    }
  }

  function refreshCarousels(changed) {
    var seen = [];
    changed.forEach(function (parent) {
      var main = parent.closest('carousel-component');
      if (main && seen.indexOf(main) === -1) seen.push(main);
      var dots = parent.closest('carousel-dots');
      if (dots && seen.indexOf(dots) === -1) seen.push(dots);
    });

    seen.forEach(function (el) {
      try {
        // reInit on the main carousel is what makes carousel-dots rebuild its
        // own embla and its dot list, so the thumbnails follow without being
        // touched directly.
        if (el.embla && typeof el.embla.reInit === 'function') {
          el.embla.reInit();
          if (typeof el.embla.scrollTo === 'function') el.embla.scrollTo(0, true);
        } else if (typeof el.reInit === 'function') {
          el.reInit();
        }
      } catch (e) {
        /* a carousel that will not re-measure is still showing the right images */
      }
    });

    // CarouselButton caches the snap count at mount and only ever refreshes it
    // from that cache, so the prev/next arrows keep hiding against the old
    // slide count unless it is put back.
    seen.forEach(function (el) {
      var buttons = el.querySelectorAll('carousel-button');
      for (var i = 0; i < buttons.length; i++) {
        try {
          var carousel = buttons[i].carousel;
          if (!carousel || !carousel.embla) continue;
          var count = carousel.embla.slideNodes().length;
          var snaps = [];
          for (var n = 0; n < count; n++) snaps.push(n);
          buttons[i].snaps = snaps;
          if (typeof buttons[i].update === 'function') buttons[i].update();
        } catch (e) {
          /* ignore */
        }
      }
    });
  }

  function filterGallery(ride) {
    collectParents();
    var wantedToken = ride.toLowerCase();
    var changed = [];

    parents.forEach(function (parent) {
      var keep = originalChildren(parent).filter(function (el) {
        var r = rideOf(el);
        // Untagged children -- the mobile thumbnail strip leads with the
        // variant's featured image -- belong to every ride.
        return r === null || r === wantedToken;
      });

      if (!sameOrder(Array.prototype.slice.call(parent.children), keep)) {
        var frag = document.createDocumentFragment();
        keep.forEach(function (el) {
          frag.appendChild(el);
        });
        parent.replaceChildren(frag);
        changed.push(parent);
      }
      renumberDots(parent);
    });

    if (changed.length) refreshCarousels(changed);
  }

  // <variant-picker> re-renders the whole block on every size / case type
  // change, so the radios that come back are the server's defaults. Put the
  // customer's choice back before anything reads it.
  //
  // There are two instances (desktop and mobile galleries are separate
  // elements) whose radios share one name and one form owner -- one radio
  // group, so the browser keeps exactly one checked across both. Checking the
  // hidden instance last would therefore uncheck the one on screen. Visible
  // roots are set last, and the selection is painted with a class as well so
  // the highlight never depends on which radio the group happened to keep.
  function syncInputs(ride) {
    var roots = Array.prototype.slice.call(document.querySelectorAll(ROOT_SELECTOR));
    roots.sort(function (a, b) {
      return (a.offsetParent ? 1 : 0) - (b.offsetParent ? 1 : 0);
    });
    roots.forEach(function (root) {
      var inputs = root.querySelectorAll(INPUT_SELECTOR);
      for (var i = 0; i < inputs.length; i++) {
        var on = inputs[i].value === ride;
        inputs[i].checked = on;
        var label = inputs[i].nextElementSibling;
        if (label && label.tagName === 'LABEL') label.classList.toggle('is-on', on);
      }
    });
  }

  // product-cs-options.liquid carries the same repair for its text fields: the
  // id an input names in form="" has drifted from the id the form actually
  // carries before now, and an unassociated input submits nothing at all.
  function syncFormAssociation() {
    var roots = document.querySelectorAll(ROOT_SELECTOR);
    for (var i = 0; i < roots.length; i++) {
      var section = roots[i].closest('.shopify-section') || document;
      var form = section.querySelector('[js-product-form]');
      if (!form || !form.id) continue;
      var inputs = roots[i].querySelectorAll(INPUT_SELECTOR);
      for (var n = 0; n < inputs.length; n++) {
        if (inputs[n].getAttribute('form') !== form.id) {
          inputs[n].setAttribute('form', form.id);
        }
      }
    }
  }

  function setRide(ride, filter) {
    current = ride;
    syncInputs(ride);
    syncFormAssociation();
    if (filter) filterGallery(ride);
  }

  // The zoom lightbox does not read the DOM. It parses the complete media list
  // out of [data-pzoom-data] on every open -- all thirteen images -- so with
  // the gallery correctly down to five, next/prev in the lightbox still walked
  // the other two rides. product-zoom-lightbox.js offers window.pzoomOverride
  // for exactly this; it is called once per open, so it always sees the ride
  // selected right now.
  //
  // CHAINED, not assigned: the colour configurator already installs an
  // override on premade products to swap in its recoloured canvas. Replacing it
  // would silently break the colour preview in the lightbox, a worse bug than
  // the one being fixed. Run whatever was there first, on the full list it
  // expects, then narrow by ride. Entries without a ride token belong to every
  // ride, and with no ride root on the page the list passes through untouched.
  function filterLightbox(list) {
    if (!Array.isArray(list) || !document.querySelector(ROOT_SELECTOR)) return list;
    var wanted = (current || defaultRide()).toLowerCase();
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var m = RIDE_RE.exec(String(list[i] && list[i].alt || '').toLowerCase());
      if (!m || m[1] === wanted) out.push(list[i]);
    }
    return out.length ? out : list;
  }

  (function chainLightboxOverride() {
    var prev = window.pzoomOverride;
    window.pzoomOverride = function (list) {
      if (typeof prev === 'function') {
        try { list = prev(list) || list; } catch (e) { /* keep the unmodified list */ }
      }
      return filterLightbox(list);
    };
    window.pzoomOverride.myocRide = true;
  })();

  function init() {
    if (!document.querySelector(ROOT_SELECTOR)) return;
    setRide(current || defaultRide(), true);
  }

  // Capture phase on the document: this runs before the bubble-phase listener
  // <variant-picker> puts on itself, and stopping here means the picker never
  // sees a change it would answer with a variant fetch. VariantPicker also
  // skips inputs whose name contains "properties", but the Add To Cart path is
  // not something to leave resting on one guard inside a minified bundle.
  function guard(event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var input = target.closest(INPUT_SELECTOR);
    if (!input || !input.closest(ROOT_SELECTOR)) return;
    event.stopPropagation();
    if (event.type === 'change') setRide(input.value, true);
  }

  document.addEventListener('change', guard, true);
  document.addEventListener('input', guard, true);

  // Fired by VariantPicker.updateElements once the replaced blocks are in.
  document.addEventListener('tcustomizer-remount', function () {
    setRide(current || defaultRide(), true);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
